/**
 * Admin Anomaly Flags Management Page
 * Dashboard for managing anomaly flags and viewing analytics
 * Implements Requirements: 2.4, 2.5 - Flag management and audit trails
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui';
import { adminAnomalyService, AnomalyFlag, AnomalyFlagFilters, AnomalyAnalytics } from '../services/admin-anomaly.service';

const AdminAnomalyFlagsPage: React.FC = () => {
  const navigate = useNavigate();
  const [flags, setFlags] = useState<AnomalyFlag[]>([]);
  const [analytics, setAnalytics] = useState<AnomalyAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Filters
  const [filters, setFilters] = useState<AnomalyFlagFilters>({
    limit: 20,
    offset: 0
  });
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  // UI State
  const [selectedFlags, setSelectedFlags] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [flagsResult, analyticsResult] = await Promise.all([
        adminAnomalyService.getFlags(filters),
        adminAnomalyService.getAnalytics({
          manufacturerId: filters.manufacturerId,
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo
        })
      ]);

      setFlags(flagsResult.flags);
      setTotal(flagsResult.total);
      setHasMore(flagsResult.hasMore);
      setAnalytics(analyticsResult);
    } catch (err: any) {
      console.error('Failed to load anomaly data:', err);
      setError(err.response?.data?.message || 'Failed to load anomaly data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters: Partial<AnomalyFlagFilters>) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      offset: 0 // Reset pagination when filters change
    }));
  };

  const handlePageChange = (newOffset: number) => {
    setFilters(prev => ({ ...prev, offset: newOffset }));
  };

  const handleFlagAction = async (flagId: string, action: 'acknowledged' | 'dismissed' | 'reopen', notes?: string) => {
    try {
      setActionLoading(flagId);
      setError(null);

      if (action === 'reopen') {
        await adminAnomalyService.reopenFlag(flagId, notes);
        setSuccess('Flag reopened successfully');
      } else {
        await adminAnomalyService.updateFlagStatus(flagId, action, notes);
        setSuccess(`Flag ${action} successfully`);
      }

      // Reload data
      await loadData();
    } catch (err: any) {
      console.error(`Failed to ${action} flag:`, err);
      setError(err.response?.data?.message || `Failed to ${action} flag`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleBulkAction = async (action: 'acknowledged' | 'dismissed') => {
    if (selectedFlags.size === 0) return;

    try {
      setActionLoading('bulk');
      setError(null);

      const promises = Array.from(selectedFlags).map(flagId =>
        adminAnomalyService.updateFlagStatus(flagId, action)
      );

      await Promise.all(promises);
      setSuccess(`${selectedFlags.size} flags ${action} successfully`);
      setSelectedFlags(new Set());

      // Reload data
      await loadData();
    } catch (err: any) {
      console.error(`Failed to ${action} flags:`, err);
      setError(err.response?.data?.message || `Failed to ${action} flags`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCleanup = async () => {
    if (!confirm('Are you sure you want to cleanup old acknowledged/dismissed flags? This action cannot be undone.')) {
      return;
    }

    try {
      setActionLoading('cleanup');
      setError(null);

      const result = await adminAnomalyService.cleanupFlags(90);
      setSuccess(`Cleaned up ${result.deletedCount} old flags`);

      // Reload data
      await loadData();
    } catch (err: any) {
      console.error('Failed to cleanup flags:', err);
      setError(err.response?.data?.message || 'Failed to cleanup flags');
    } finally {
      setActionLoading(null);
    }
  };

  const toggleFlagSelection = (flagId: string) => {
    const newSelection = new Set(selectedFlags);
    if (newSelection.has(flagId)) {
      newSelection.delete(flagId);
    } else {
      newSelection.add(flagId);
    }
    setSelectedFlags(newSelection);
  };

  const selectAllFlags = () => {
    if (selectedFlags.size === flags.length) {
      setSelectedFlags(new Set());
    } else {
      setSelectedFlags(new Set(flags.map(f => f.id)));
    }
  };

  if (loading && flags.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading anomaly flags...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-gray-900">
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Anomaly Flags Management</h1>
            <p className="text-sm text-gray-600 mt-1">
              Monitor and manage anomaly detection flags
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => navigate('/admin/anomaly/policy')}>
              Policy Settings
            </Button>
            <Button variant="outline" onClick={() => navigate('/admin')}>
              Back to Admin
            </Button>
          </div>
        </div>

        {/* Status Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex">
              <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-red-800">{error}</p>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex">
              <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <p className="text-green-800">{success}</p>
            </div>
          </div>
        )}

        {/* Analytics Cards */}
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Flags</p>
                  <p className="text-3xl font-bold text-gray-900">{analytics.totalFlags}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Open Flags</p>
                  <p className="text-3xl font-bold text-red-600">{analytics.openFlags}</p>
                </div>
                <div className="p-3 bg-red-100 rounded-full">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">High Risk</p>
                  <p className="text-3xl font-bold text-orange-600">{analytics.highRiskFlags}</p>
                </div>
                <div className="p-3 bg-orange-100 rounded-full">
                  <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Resolution Rate</p>
                  <p className="text-3xl font-bold text-green-600">{analytics.flagResolutionRate}%</p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters and Actions */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Flags ({total})</h3>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
              >
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </Button>
              {selectedFlags.size > 0 && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => handleBulkAction('acknowledged')}
                    disabled={actionLoading === 'bulk'}
                  >
                    Acknowledge Selected ({selectedFlags.size})
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleBulkAction('dismissed')}
                    disabled={actionLoading === 'bulk'}
                  >
                    Dismiss Selected ({selectedFlags.size})
                  </Button>
                </>
              )}
              <Button
                variant="outline"
                onClick={handleCleanup}
                disabled={actionLoading === 'cleanup'}
              >
                {actionLoading === 'cleanup' ? 'Cleaning...' : 'Cleanup Old Flags'}
              </Button>
              <Button onClick={loadData} disabled={loading}>
                {loading ? 'Refreshing...' : 'Refresh'}
              </Button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={filters.status || ''}
                  onChange={(e) => handleFilterChange({ status: e.target.value as any || undefined })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Statuses</option>
                  <option value="open">Open</option>
                  <option value="acknowledged">Acknowledged</option>
                  <option value="dismissed">Dismissed</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Risk Level</label>
                <select
                  value={filters.riskLevel || ''}
                  onChange={(e) => handleFilterChange({ riskLevel: e.target.value as any || undefined })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Risk Levels</option>
                  <option value="high">High Risk</option>
                  <option value="medium">Medium Risk</option>
                  <option value="low">Low Risk</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date From</label>
                <input
                  type="date"
                  value={filters.dateFrom ? filters.dateFrom.toISOString().split('T')[0] : ''}
                  onChange={(e) => handleFilterChange({ dateFrom: e.target.value ? new Date(e.target.value) : undefined })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date To</label>
                <input
                  type="date"
                  value={filters.dateTo ? filters.dateTo.toISOString().split('T')[0] : ''}
                  onChange={(e) => handleFilterChange({ dateTo: e.target.value ? new Date(e.target.value) : undefined })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}
        </div>

        {/* Flags Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200" aria-label="Anomaly flags">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedFlags.size === flags.length && flags.length > 0}
                    onChange={selectAllFlags}
                    className="rounded border-gray-300"
                    aria-label="Select all anomaly flags"
                  />
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product / Risk
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reasons
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {flags.map((flag) => (
                <tr key={flag.id} className={selectedFlags.has(flag.id) ? 'bg-blue-50' : ''}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedFlags.has(flag.id)}
                      onChange={() => toggleFlagSelection(flag.id)}
                      className="rounded border-gray-300"
                      aria-label={`Select anomaly flag ${flag.id}`}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        Product: {flag.productId.substring(0, 8)}...
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${adminAnomalyService.getRiskLevelColor(flag.riskLevel)}`}>
                          {adminAnomalyService.formatRiskLevel(flag.riskLevel)}
                        </span>
                        <span className="text-sm text-gray-500">Score: {flag.riskScore}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${adminAnomalyService.getFlagStatusColor(flag.status)}`}>
                      {adminAnomalyService.formatFlagStatus(flag.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 max-w-xs">
                      {flag.reasons.slice(0, 2).map((reason, index) => (
                        <div key={index} className="truncate">{reason}</div>
                      ))}
                      {flag.reasons.length > 2 && (
                        <div className="text-gray-500">+{flag.reasons.length - 2} more</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(flag.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex gap-2">
                      {flag.status === 'open' && (
                        <>
                          <button
                            onClick={() => handleFlagAction(flag.id, 'acknowledged')}
                            disabled={actionLoading === flag.id}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Acknowledge
                          </button>
                          <button
                            onClick={() => handleFlagAction(flag.id, 'dismissed')}
                            disabled={actionLoading === flag.id}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            Dismiss
                          </button>
                        </>
                      )}
                      {flag.status !== 'open' && (
                        <button
                          onClick={() => handleFlagAction(flag.id, 'reopen')}
                          disabled={actionLoading === flag.id}
                          className="text-green-600 hover:text-green-900"
                        >
                          Reopen
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {flags.length === 0 && !loading && (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No anomaly flags</h3>
              <p className="mt-1 text-sm text-gray-500">No flags match your current filters.</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {(hasMore || filters.offset! > 0) && (
          <div className="bg-white rounded-lg shadow p-6 mt-6">
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-600">
                Showing {filters.offset! + 1} to {Math.min(filters.offset! + flags.length, total)} of {total} flags
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => handlePageChange(Math.max(0, filters.offset! - filters.limit!))}
                  disabled={filters.offset === 0}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handlePageChange(filters.offset! + filters.limit!)}
                  disabled={!hasMore}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default AdminAnomalyFlagsPage;

